import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useUserAuth } from '../../../context/UserAuthContext';

/**
 * Custom hook for managing survey data and API interactions
 * 
 * This hook handles:
 * - Fetching survey structure from the database
 * - Loading saved survey responses 
 * - Loading saved images
 * - User and store information management
 * 
 * @returns {Object} Survey data state and functions
 */
export const useSurveyData = () => {
  // Get Firebase user from auth context
  const { user: firebaseUser } = useUserAuth();

  // Survey structure and metadata
  const [surveyInfo, setSurveyInfo] = useState(null);
  const [surveyId, setSurveyId] = useState(null);
  
  // User and store information
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    cell: '',
    role: '',
    defaultStore: '',
    storeName: '',
    department: '',
  });
  const [stores, setStores] = useState([]);
  
  // Saved data from previous sessions
  const [savedAnswers, setSavedAnswers] = useState(null);
  const [savedImages, setSavedImages] = useState(null);
  const [frIDs, setFrIDs] = useState({}); // Form record IDs for updates vs inserts
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetches the survey structure and questions from the database
   * This defines what sections, areas, and questions will be displayed
   */
  const fetchSurveyInfo = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return;

    try {
      setLoading(true);
      setError(null);

      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get('/hd-api/facilities-walkthrough/info', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Handle the new API response structure that wraps data in { success, data, message }
      const surveyData = response.data.data || response.data;
      setSurveyInfo(surveyData);
    } catch (err) {
      setError('Failed to load survey structure. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [firebaseUser]);

  /**
   * Fetches user information including their profile and default store
   */
  const fetchUserInfo = useCallback(async () => {
    if (!firebaseUser?.email) return;

    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/user/${firebaseUser.email}/info`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.newUser) {
        // Redirect to profile setup if user hasn't completed their profile
        window.location.href = '/profile';
        return;
      }

      setUserInfo(response.data.userInfo);
    } catch (err) {
      setError('Failed to load user information.');
    }
  }, [firebaseUser]);

  /**
   * Fetches available stores for the current user
   */
  const fetchStores = useCallback(async () => {
    if (!firebaseUser?.email) return;

    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/user/stores/${firebaseUser.email}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setStores(response.data.stores);
    } catch (err) {
      setError('Failed to load available stores.');
    }
  }, [firebaseUser]);

  /**
   * Loads previously saved survey responses for a specific section
   * @param {number} section - The section number to load (1-based index)
   */
  const fetchSavedAnswers = useCallback(async (section) => {
    if (!firebaseUser?.email || !userInfo.defaultStore) {
      return;
    }

    try {
      const token = await firebaseUser.getIdToken(true);
      const url = `/hd-api/facilities-walkthrough/user/${firebaseUser.email}/store/${userInfo.defaultStore}/section/${section}/answers/saved`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Handle API response structure - the response contains { success, data: { answers, images } }
      const responseData = response.data.data || response.data;
      const answersData = responseData.answers || responseData;
      
      
      if (answersData && answersData.length > 0) {
        setSavedAnswers(answersData);
        
        // Extract form record IDs for future updates
        const newFrIDs = {};
        answersData.forEach(({ formName, frID }) => {
          if (frID) {
            newFrIDs[formName] = frID;
          }
        });
        setFrIDs(newFrIDs);
        
        // Also handle saved images if present
        if (responseData.images && responseData.images.length > 0) {
          setSavedImages(responseData.images);
        } else {
          setSavedImages(null);
        }
        
        // Return the answers data for use by caller
        return answersData;
      } else {
        setSavedAnswers(null);
        setSavedImages(null);
        return null;
      }
    } catch (err) {
      // Don't set error state here as this is optional data
      return null;
    }
  }, [firebaseUser, userInfo.defaultStore]);

  /**
   * Loads previously saved images for the current store and user
   */
  const fetchSavedImages = useCallback(async () => {
    if (!firebaseUser?.email || !userInfo.defaultStore) return;

    try {
      const token = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/facilities-walkthrough/user/${firebaseUser.email}/store/${userInfo.defaultStore}/images/saved`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Handle the new API response structure
      const imagesData = response.data.data || response.data;
      
      if (imagesData && imagesData.length > 0) {
        setSavedImages(imagesData);
      }
    } catch (err) {
      // Don't set error state here as this is optional data
    }
  }, [firebaseUser, userInfo.defaultStore]);

  /**
   * Archives (deletes) saved survey data for the current user and store
   * This is called when user chooses "Start New" instead of restoring
   */
  const archiveSavedSurvey = useCallback(async () => {
    if (!firebaseUser?.email || !userInfo.defaultStore) return;

    try {
      const token = await firebaseUser.getIdToken(true);
      await axios.delete(
        `/hd-api/facilities-walkthrough/user/${firebaseUser.email}/store/${userInfo.defaultStore}/saved`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Clear saved data from state
      setSavedAnswers(null);
      setSavedImages(null);
      setFrIDs({});
    } catch (err) {
      setError('Failed to archive previous survey data.');
    }
  }, [firebaseUser, userInfo.defaultStore]);

  /**
   * Updates the user's selected store information
   * @param {string} storeId - The ID of the newly selected store
   * @param {string} storeName - The name of the newly selected store
   */
  const updateSelectedStore = useCallback((storeId, storeName) => {
    setUserInfo(prev => ({
      ...prev,
      defaultStore: storeId,
      storeName: storeName,
    }));

    // Clear saved data when store changes
    setSavedAnswers(null);
    setSavedImages(null);
    setFrIDs({});
  }, []);

  // Initialize data when component mounts or user changes
  useEffect(() => {
    if (firebaseUser?.getIdToken) {
      fetchSurveyInfo();
      fetchUserInfo();
      fetchStores();
    }
  }, [firebaseUser, fetchSurveyInfo, fetchUserInfo, fetchStores]);

  // Return all state and functions for use in components
  return {
    // Survey structure
    surveyInfo,
    surveyId,
    setSurveyId,
    
    // User and store data
    userInfo,
    stores,
    updateSelectedStore,
    
    // Saved data
    savedAnswers,
    savedImages,
    frIDs,
    
    // Loading and error states
    loading,
    error,
    
    // Functions
    fetchSavedAnswers,
    fetchSavedImages,
    archiveSavedSurvey,
    
    // State setters for external use
    setSavedAnswers,
    setSavedImages,
    setFrIDs,
  };
};